import * as React from 'react';
import PageModel from '@app/Model/PageModel';
import { Flex, FlexItem, List, ListItem, PageSection, Title } from '@patternfly/react-core';
import ArticleHelper from '@app/Service/ArticleHelper';
import ArticleInterface from '@app/Interface/ArticleInterface';
import PageLoader from '@app/Page/PageLoader';
import ArticleItem from '@app/Components/Article/ArticleItem';
import htmlParser from '@app/utils/htmlParser';
import { Link } from 'react-router-dom';
import ArticleItemDetail from '@app/Components/Article/ArticleItemDetail';
import ArticleCategoryInterface from '@app/Interface/ArticleCategoryInterface';

interface ArticlePageProps {
    fpuId: number,
    pageData: PageModel,
    path: string,
    search: string
}

const ArticlePage: React.FC<ArticlePageProps> = ({ fpuId, pageData, path, search }) => {

    // [How to use types with useState](https://linguinecode.com/post/how-to-use-react-usestate-with-typescript)

    // a list of ALL articles
    const [articles, setArticles] = React.useState<ArticleInterface[]>([]);

    // a list of Categories
    const [categories, setCategories] = React.useState<ArticleCategoryInterface[]>([]);

    // filter list of articles by category or show all articles
    const [category, setCategory] = React.useState("");
    const [articlesToView, setArticlesToView] = React.useState<ArticleInterface[]>([]);

    // Get selected article based on slug 
    const [articleSlug, setArticleSlug] = React.useState(htmlParser.getParameterValueFromUrl("article", search));
    const [articleDetail, setArticleDetail] = React.useState<ArticleInterface | null>(null);

    const [loadingArticles, setLoadingArticles] = React.useState(true);
    const [articlePath, setArticlePath] = React.useState("");

    // get articles once
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        // TODO: Add caching techniques
        async function getArticles() {

            const articles = await ArticleHelper.getArticles(controller, fpuId, pageData.id);

            if (mounted) {
            
                setArticles(articles);
                setLoadingArticles(false);
            }

        }

        if (fpuId && pageData.id) {
            getArticles();
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [fpuId, pageData.id]);

    // get article categories
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        // TODO: Cache article categories
        async function getArticleCategories() {

            const articleCategories = await ArticleHelper.getArticleCategories(controller);

            if (mounted) {
                setCategories(articleCategories);
            }
            
        }

        getArticleCategories();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    React.useEffect(() => {

        const category = htmlParser.getParameterValueFromUrl("cat", search);

        const articleSlug = htmlParser.getParameterValueFromUrl("article", search);
        
        const strippedPath = htmlParser.removeQuerystringFromPath(path);
        
        setCategory(category);

        setArticleSlug(articleSlug);

        setArticlePath(strippedPath);

        // set default values first
        setArticleDetail(null);

        setArticlesToView([]);

    }, [path, search]);

    React.useEffect(() => {

        if (category) {

            const filteredArticles = ArticleHelper.filterArticlesByCategory(articles, category);

            setArticlesToView(filteredArticles);

        } else {

            setArticlesToView(articles);
        }

        if (articleSlug) {

            const articleMatch = ArticleHelper.getArticleBySlug(articles, articleSlug);

            setArticleDetail(articleMatch);

        }

    }, [articles, category, articleSlug]);

    // Scroll to the top of the page when articleDetail changes
    React.useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, [articleDetail]);

    // Special case where the user loaded the article details page directly
    if (loadingArticles && articleSlug) {
        return <PageLoader />;
    }

    if (articleDetail) {

        return (
            <>
                {loadingArticles
                    ? <PageLoader />
                    : <ArticleItemDetail article={articleDetail} path={articlePath} categoryFilter={category} />
                }
            </>
        );

    } else {

        return (
            <Flex>
                <Flex flex={{ default: "flex_2" }} direction={{ default: "column" }}>
                    <FlexItem>
                        <PageSection>
                            <Title headingLevel="h1" className="pf-u-mb-md">{pageData.title}</Title>
                            <div className="pf-c-content">
                                {htmlParser.htmlToJSX(pageData.content ?? '')}
                            </div>
                        </PageSection>
                        {loadingArticles ? <PageLoader /> :
                            <PageSection>
                                {articlesToView.map((article, index) => (
                                    <ArticleItem key={index} article={article} path={articlePath} />
                                ))}
                            </PageSection>
                        }
                    </FlexItem>
                </Flex>
                <Flex flex={{ default: "flex_1" }} direction={{ default: "column" }}>
                    <FlexItem>
                        <div>
                            <Title headingLevel="h2">Categories</Title>
                            <List isPlain isBordered>
                                <ListItem key="0">
                                    <Link to={{ pathname: path, search: `` }}>All</Link>
                                </ListItem>
                                {categories.map(category => (
                                    <ListItem key={category.id}>
                                        <Link to={{ pathname: path, search: `?cat=${category.slug}` }}>{category.name}</Link>
                                    </ListItem>
                                ))}
                            </List>
                        </div>
                    </FlexItem>
                </Flex>
            </Flex>
        )
    }

};

export default ArticlePage;